import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useCompanyPageTitle } from '@user/features/CompanyIdentifier/hooks'

import {
  DraftLeadCardList,
  DraftLeadListView,
  DraftLeadPageFilterButton,
} from '../DraftLeads'
import { LeadCardList } from './LeadCardList'
import { EntityTypeTabs } from './EntityTypeTabs'
import { LeadListView } from './LeadList'
import { LeadPageFilterButton } from './LeadPageFilterButton'
import {
  CardListViewPicker,
  PageHeader,
  Pagination,
  SettingsPage,
  toast,
} from '@sales-center/components'

import {
  getDraftLeadList,
  selectDraftLeadFilters,
  selectDraftLeadPageState,
  setDraftLeadPage,
  setDraftLeadPageMode,
  setDraftLeadsPerPage,
} from '@user/store/draftLeads'
import {
  getLeadList,
  HistoryRoutePathnames,
  selectLeadFilters,
  selectLeadPageState,
  setLeadPage,
  setLeadPageMode,
  setLeadsPerPage,
} from '@user/store/lead'

import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  selectUserPreferencesGeneralLoaded,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'

import {
  selectDraftLeadColumnOrder,
  selectLeadColumnOrder,
} from '@user/store/userPreferences'
import { defaultLeadColumns } from './LeadList/leadListColumns'
import { defaultDraftLeadColumns } from '../DraftLeads/DraftLeadListView/draftLeadColumns'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

import { getLeadListExport } from '@user/store/lead/api'
import { getDraftListExport } from '@user/store/draftLeads/api'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { LeadPageFilterChipList } from './LeadPageFilterChipList'
import { DraftLeadFilterChipList } from '../DraftLeads/Filters'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import { updateLoadingState } from '@sales-center/common'

const getStyles = (theme: Theme) => ({
  header: {
    fontSize: '16px',
    fontFamily: 'Source Sans Pro Semibold',
  },

  listWrapper: {
    maxWidth: theme.breakpoints.values.lg,
    width: '100%',
  },
})

interface Props {
  isDraft: boolean
  canUpdateLeads: boolean
  canDeleteLeads: boolean
  canConvertLeads: boolean
}

export const LeadPage: FC<Props> = (props) => {
  const { isDraft, canConvertLeads, canDeleteLeads, canUpdateLeads } = props
  const dispatch = useAppDispatch()

  const { page, pages, perPage, loading, count, shouldFetchList } = useSelector(
    isDraft ? selectDraftLeadPageState : selectLeadPageState,
  )
  const { filterSet: draftFilters } = useSelector(selectDraftLeadFilters)
  const { filterSet: leadFilters } = useSelector(selectLeadFilters)
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const defaultListViewUserGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.ListMode,
  )

  const mode =
    defaultListViewUserGeneralPreference?.value == 'list'
      ? 'list'
      : defaultListViewUserGeneralPreference?.value == 'map'
        ? 'map'
        : 'card'

  const defaultLeadFilterShow = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.LeadFilterShow,
  )
  const showFilters = defaultLeadFilterShow?.value != 'hide'

  const leadColumnOrder = useSelector(selectLeadColumnOrder)
  const draftColumnOrder = useSelector(selectDraftLeadColumnOrder)

  const headerText = useMemo(() => {
    return `${count} ${isDraft ? 'Pending' : ''} ${
      count == 1 ? 'Lead' : 'Leads'
    } found`
  }, [count, isDraft])

  const appliedCount = isDraft
    ? draftFilters.appliedCount
    : leadFilters.appliedCount

  const onChangePage = React.useCallback(
    (_e: React.ChangeEvent<unknown>, newPage: number) => {
      sessionStorage.setItem('pageNumber', newPage?.toString())
      dispatch(isDraft ? setDraftLeadPage(newPage) : setLeadPage(newPage))
      document
        .getElementById('topScrollIndicator')
        ?.scrollIntoView({ block: 'end', behavior: 'smooth' })
    },
    [dispatch, isDraft],
  )

  const pageNumberPersistent = useCallback(() => {
    const defaultPerPageMode = mode == 'card' ? 10 : perPage

    const previousPathname = sessionStorage.getItem('previousPathname')
    const currentPathname = sessionStorage.getItem('currentPathname')
    const pageNumber = sessionStorage.getItem('pageNumber') ?? '1'
    if (
      currentPathname?.includes(HistoryRoutePathnames.DRAFT_LEADS) &&
      previousPathname?.includes(HistoryRoutePathnames.DRAFT_LEAD_ADD) &&
      pageNumber
    ) {
      dispatch(setDraftLeadPage(parseInt(pageNumber)))
      return
    }

    if (
      currentPathname?.includes(HistoryRoutePathnames.LEADS) &&
      previousPathname?.includes(HistoryRoutePathnames.LEAD_DETAILS) &&
      pageNumber
    ) {
      dispatch(setLeadPage(parseInt(pageNumber)))
      return
    }

    sessionStorage.setItem('pageNumber', '1')
    sessionStorage.setItem('pageSize', defaultPerPageMode?.toString())
  }, [dispatch, mode, perPage])

  useEffect(() => {
    dispatch(isDraft ? setDraftLeadPageMode(mode) : setLeadPageMode(mode))
  }, [mode, dispatch, isDraft])

  // Restore the user's saved "Rows per page" for the list view across refresh.
  // List mode otherwise resets perPage to the default; we re-apply the persisted
  // size when entering list mode (card view has no rows-per-page selector). Keyed
  // off the previous mode so it fires on entering list — not on every preference
  // change — which would fight the user's optimistic change during the async save.
  const prefsLoaded = useSelector(selectUserPreferencesGeneralLoaded)
  const pageSizePrefType = isDraft
    ? IUserPreferenceGeneralListItemType.PendingLeadPageSize
    : IUserPreferenceGeneralListItemType.LeadPageSize
  const persistedPageSizeValue = userGeneralPreferences?.find(
    (row) => row?.type == pageSizePrefType,
  )?.value
  const prevModeRef = useRef<string | null>(null)

  useEffect(() => {
    const persistedPageSize = Number(persistedPageSizeValue)
    if (
      prefsLoaded &&
      mode === 'list' &&
      prevModeRef.current !== 'list' &&
      persistedPageSize
    ) {
      dispatch(
        isDraft
          ? setDraftLeadsPerPage(persistedPageSize)
          : setLeadsPerPage(persistedPageSize),
      )
    }
    prevModeRef.current = mode
  }, [prefsLoaded, mode, persistedPageSizeValue, isDraft, dispatch])

  useEffect(() => {
    if (pages != 0) {
      pageNumberPersistent()
    }
  }, [pageNumberPersistent, pages])

  const onClickCardMode = React.useCallback(() => {
    sessionStorage.setItem('pageNumber', '1')
    sessionStorage.setItem('pageSize', '10')
    if (mode !== 'card') {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ListMode,
          value: 'card',
        }),
      )
    }
  }, [dispatch, mode])

  const onClickListMode = React.useCallback(() => {
    sessionStorage.setItem('pageNumber', '1')
    sessionStorage.setItem('pageSize', '50')
    if (mode !== 'list') {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ListMode,
          value: 'list',
        }),
      )
    }
  }, [dispatch, mode])

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2DKnockEnabled = useD2DKnockEnabled()
  const knockStatusEnabled = mapViewEnabled && d2DKnockEnabled

  const onClickExport = React.useCallback(
    async (multiSLSelected: boolean) => {
      const columnOrder = isDraft ? draftColumnOrder : leadColumnOrder

      const columns = columnOrder
        .filter((x) => x.hide === false)
        .map((x) => x.field)

      // Include knock status columns if the feature is on but the user's saved
      // column order hasn't been synced with them yet (first export before the
      // useAddMissingColumns hook has persisted the new fields).
      const missingKnockStatusFields = knockStatusEnabled
        ? ['knockStatus', 'knockStatusReason'].filter(
            (f) => !columnOrder.some((c) => c.field === f),
          )
        : []

      const baseColumns =
        columns && columns.length > 0
          ? columns
          : isDraft
            ? defaultDraftLeadColumns
            : defaultLeadColumns
      const finalColumns = [...baseColumns, ...missingKnockStatusFields]

      dispatch(updateLoadingState('visible'))
      const data = isDraft
        ? await dispatch(getDraftListExport(finalColumns))
        : await dispatch(
            getLeadListExport({
              columns: finalColumns,
              multiServiceLocations: multiSLSelected,
            }),
          )

      if (
        getDraftListExport.fulfilled.match(data) ||
        getLeadListExport.fulfilled.match(data)
      ) {
        dispatch(updateLoadingState('hidden'))
        return data.payload
      } else {
        dispatch(updateLoadingState('hidden'))
        toast.error('An Error occurred.  Unable to Export List.')
      }
    },
    [dispatch, draftColumnOrder, leadColumnOrder, isDraft, knockStatusEnabled],
  )

  useEffect(() => {
    if (shouldFetchList) {
      const cardCaptureRefresh = mode === 'list' ? false : true
      if (isDraft) {
        dispatch(getDraftLeadList())
      } else {
        dispatch(getLeadList(cardCaptureRefresh))
      }
    }
  }, [isDraft, dispatch, shouldFetchList, mode])

  const [filtersDisabled, setFiltersDisabled] = useState(false)

  useEffect(() => setFiltersDisabled(false), [mode])

  const onShowFilters = useCallback(
    (_e, checked: boolean) => {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.LeadFilterShow,
          value: checked ? 'show' : 'hide',
        }),
      )
    },
    [dispatch],
  )
  const theme = useTheme()
  const styles = getStyles(theme)
  const pageTitle = useCompanyPageTitle()
  return (
    <SettingsPage
      {...pageTitle}
      padding={mode === 'card' ? '20px 40px 20px 40px' : '0 0 0 0'}
      headContent={
        <PageHeader
          alignItems="flex-end"
          noPadding
          flexDirection="row-reverse"
          flexWrap="wrap"
        >
          <Box marginLeft="auto" px={2} py={1}>
            <Box display="flex" alignItems="center">
              {isDraft ? (
                <DraftLeadPageFilterButton
                  mode={mode}
                  disabled={filtersDisabled}
                />
              ) : (
                <LeadPageFilterButton mode={mode} disabled={filtersDisabled} />
              )}

              <CardListViewPicker
                cardClickCallback={onClickCardMode}
                listClickCallback={onClickListMode}
                mode={mode}
              />
            </Box>
          </Box>
          <EntityTypeTabs />
        </PageHeader>
      }
    >
      <Stack flexDirection="column" alignItems="center" height="100%">
        <Box
          maxWidth={
            mode == 'card' ? theme.breakpoints?.values?.lg || '1200px' : 'auto'
          }
          width="100%"
          pr={mode == 'list' ? '20px' : '0'}
        >
          <Box display="flex" mb={0.7} mt={mode == 'card' ? 0 : 2}>
            <Box display="flex" flexGrow={1} ml={mode == 'card' ? 0 : 2}>
              <Typography sx={styles.header} data-test-id="cardViewHeader">
                {headerText}
              </Typography>
            </Box>
            <Box>
              {!filtersDisabled && appliedCount > 0 && (
                <Box display="flex" alignItems="center">
                  <Switch
                    size="small"
                    checked={showFilters}
                    onChange={onShowFilters}
                    onClick={(evt) => evt.stopPropagation()}
                    data-test-id="showFilterSelectionSwitch"
                  />
                  <Typography variant="body2">Show Filter Selection</Typography>
                </Box>
              )}
            </Box>
          </Box>
          {!loading &&
            showFilters &&
            appliedCount > 0 &&
            (mode == 'card' || (mode == 'list' && !filtersDisabled)) && (
              <Box display="flex" mb={2}>
                {' '}
                {isDraft ? (
                  <DraftLeadFilterChipList />
                ) : (
                  <LeadPageFilterChipList />
                )}
              </Box>
            )}
        </Box>
        {mode === 'list' ? (
          isDraft ? (
            <DraftLeadListView
              exportClickCallback={onClickExport}
              loading={loading}
              count={count}
              page={page}
              perPage={perPage}
              setFiltersDisabled={setFiltersDisabled}
            />
          ) : (
            <LeadListView
              exportClickCallback={onClickExport}
              loading={loading}
              count={count}
              page={page}
              perPage={perPage}
              setFiltersDisabled={setFiltersDisabled}
            />
          )
        ) : mode === 'card' ? (
          <Box sx={styles.listWrapper}>
            <Box>
              <span id="topScrollIndicator"></span>
              {isDraft ? (
                <DraftLeadCardList
                  loading={loading}
                  canDelete={canDeleteLeads}
                />
              ) : (
                <LeadCardList
                  loading={loading}
                  canUpdate={canUpdateLeads}
                  canDelete={canDeleteLeads}
                  canConvert={canConvertLeads}
                />
              )}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={1} mb={0.5}>
              <Pagination
                page={page}
                pages={pages}
                onChangePage={onChangePage}
              />
            </Box>
          </Box>
        ) : null}
      </Stack>
    </SettingsPage>
  )
}
LeadPage.displayName = 'LeadPage'
